import { ensureEndsWith, getErrorSwal } from '@asamco/utils';
import { LogLevel, StsConfigHttpLoader } from 'angular-auth-oidc-client';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppSettings } from '../configuration/appsettings';
import { OidcConfigSetupService } from './oidc-config-setup.service';
import Swal from 'sweetalert2';

function deleteOidcDataWhenPolicyIsDifferent(options: { clientId: string; currentPolicy: string }) {
	try {
		const authWellKnownEndPointsString = localStorage.getItem(`${options.clientId}_authWellKnownEndPoints`);
		if(authWellKnownEndPointsString) {
			const authWellKnownEndPoints = JSON.parse(authWellKnownEndPointsString);
			if (authWellKnownEndPoints['authorizationEndpoint'] && typeof authWellKnownEndPoints['authorizationEndpoint'] === 'string') {
				const url = new URL(authWellKnownEndPoints['authorizationEndpoint']);
			if (url.searchParams.has('p') && url.searchParams.get('p').toLocaleLowerCase() !== options.currentPolicy.toLocaleLowerCase()) {
				Object.keys(localStorage)
					.filter(k => k.startsWith(`${options.clientId}_`))
						.forEach(k => localStorage.removeItem(k));
				}
			}
		}
	} catch (error) {
		console.error(error);
	}
	// Below added later. Above was for older version of oidc lib.
	try {
		let reset = false;
		const configJsonString = sessionStorage.getItem('configId');
		if(configJsonString) {
			const configJson = JSON.parse(configJsonString);
			if (configJson['authWellKnownEndPoints'] && typeof configJson['authWellKnownEndPoints'] === 'object'
			) {
				for (const key in configJson['authWellKnownEndPoints']) {
					if (configJson['authWellKnownEndPoints'].hasOwnProperty(key)) {
						const url = new URL(configJson['authWellKnownEndPoints'][key]);
						if (url.searchParams.has('p') && url.searchParams.get('p').toLocaleLowerCase() !== options.currentPolicy.toLocaleLowerCase()) {
							reset = true;
						}
					}
				}
			}
		}
		if (reset === true) {
			sessionStorage.removeItem('configId');
		}
	} catch (error) {
		console.error(error);
	}
}

export function loadOidcConfig(app: AppSettings, oidcConfigSetupService: OidcConfigSetupService) {

	// If useRemoteConfig is set to true
	// use an API call to fetch the B2C settings
	// else use the settings from the config.
	const configGetter$ = oidcConfigSetupService
		.getConfig()
		.pipe(
			catchError(err => {
				const errSwal = getErrorSwal(err);
				Swal.fire(errSwal);
				// this process should still fail. We cannot recover.
				return throwError(() => new Error(errSwal.text));
			}),
			map(result => {
				const clientId = result.clientId;

				const policy = result.policies.find(p => p.isDefault === true);

				const policyName = policy?.policyName ?? '';
				const tenant = result.tenant;

				// Get the host url from the window
				const hostUrl = ensureEndsWith(window.location.origin, '/');

				deleteOidcDataWhenPolicyIsDifferent({ clientId, currentPolicy: policyName });

				const config = {
					configId: 'configId',
					authority: `https://${tenant}.b2clogin.com/${tenant}.onmicrosoft.com/${policyName}`,
					redirectUrl: hostUrl,
					postLogoutRedirectUri: hostUrl,
					postLoginRoute: 'auth/post-login',
					autoCleanStateAfterAuthentication: false,
					clientId: clientId,
					responseType: 'code',
					maxIdTokenIatOffsetAllowedInSeconds: app.settings.b2c.maxAllowedIATOffset,
					storage: window.localStorage,

					unauthorizedRoute: '/auth/unauthorized',
					forbiddenRoute: '/auth/forbidden',

					// offline_access is needed for refresh token to work
					scope: `openid offline_access https://${tenant}.onmicrosoft.com${app.settings.b2c.scope}`,
					//scope: 'openid profile offline_access',

					// When not set to false will not refresh. Not 100% clear why but it is
					// working as intended when following the source code.
					// My assumption is that autoUserInfo = true would trigger a different
					// mechanism to refresh the token.
					autoUserInfo: false,

					// Custom well known endpoints url since azure uses a different one as the sts server
					authWellknownEndpointUrl: `https://${tenant}.b2clogin.com/${tenant}.onmicrosoft.com/v2.0/.well-known/openid-configuration?p=${policyName}`,

					// Logging
					logLevel: window['al_oidc_debug'] === true ? LogLevel.Debug : LogLevel.Error,

					// nonce claim SHOULD not be present for the refesh_token grant type
					// https://bitbucket.org/openid/connect/issues/1025/ambiguity-with-how-nonce-is-handled-on
					ignoreNonceAfterRefresh: true,
					// disables the auth_time validation for id_tokens in a refresh due to Azure incorrect implementation
					// see: https://github.com/damienbod/angular-auth-oidc-client/blob/master/docs/configuration.md
					disableRefreshIdTokenAuthTimeValidation: true,

					// triggerRefreshWhenIdTokenExpired: false,

					// Silent renew
					silentRenew: true,
					useRefreshToken: true,
					renewTimeBeforeTokenExpiresInSeconds: app.settings.b2c.silentRenewalOffset,
				};
				console.log('OIDC config built.');
				return config;
			})
		);

	// use http loader to accommodate async retrieval
	return new StsConfigHttpLoader(configGetter$);
}
